import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getApiError } from "../../services/courseApi";
import { decideCoursePayment, getCoursePayment, listUsers } from "../../services/coursePaymentApi";
import { decisionPayload, displayDate, fullName, money, userMap } from "./coursePaymentUtils";
import "./coursePayment.css";

export default function CoursePaymentDetail() {
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(null);
  const [guruId, setGuruId] = useState("");
  const [reason, setReason] = useState("");

  useEffect(() => {
    Promise.all([getCoursePayment(paymentId), listUsers()])
      .then(([payment, userResult]) => { setItem(payment); setUsers(userResult); })
      .catch((error) => toast.error(getApiError(error, "Could not load payment")))
      .finally(() => setLoading(false));
  }, [paymentId]);

  const student = useMemo(() => userMap(users).get(Number(item?.student_id)), [users, item]);
  const isSubmitted = item?.payment?.status === "SUBMITTED";
  const isOrganization = item?.provider?.type === "ORGANIZATION";

  async function submitDecision(event) {
    event.preventDefault();
    if (mode === "VERIFIED" && isOrganization && !guruId) {
      toast.error("Select the guru who will own this enrollment"); return;
    }
    if (mode === "REJECTED" && !reason.trim()) {
      toast.error("Enter a rejection reason"); return;
    }
    setSaving(true);
    try {
      const updated = await decideCoursePayment(paymentId, decisionPayload(mode, { guruId, reason }));
      setItem((current) => ({ ...current, ...updated, eligible_gurus: current.eligible_gurus }));
      setMode(null); setReason(""); setGuruId("");
      toast.success(mode === "VERIFIED" ? "Payment verified and enrollment activated" : "Payment rejected");
    } catch (error) {
      toast.error(getApiError(error, "Could not update payment"));
    } finally { setSaving(false); }
  }

  if (loading) return <main className="course-page"><div className="course-state"><div className="spinner-border text-primary" /></div></main>;
  if (!item) return <main className="course-page"><div className="course-state"><h1>Payment not found</h1>
    <Link to="/course-payments">Back to payments</Link></div></main>;

  return <main className="course-page payment-page">
    <button className="course-back-link btn btn-link px-0 mb-3" onClick={() => navigate(-1)}>
      <i className="bi bi-arrow-left me-2" />Back to course payments</button>
    <div className="course-page-header"><div><p className="course-eyebrow">Payment review</p>
      <h1>{item.course?.title}</h1><p className="text-muted mb-0">Payment #{item.payment?.payment_id} · Enrollment #{item.enrollment_id}</p></div>
      <span className={`payment-status large ${String(item.payment?.status).toLowerCase()}`}>{item.payment?.status}</span></div>

    <section className="payment-highlight">
      <div><span>Amount submitted</span><strong>{money(item.payment?.amount, item.payment?.currency)}</strong>
        <small>{item.payment?.total_sessions} sessions · {item.payment?.remaining_sessions} remaining</small></div>
      {isSubmitted && <div className="payment-actions">
        <button className="btn btn-outline-danger" onClick={() => setMode("REJECTED")}>Reject</button>
        <button className="btn btn-primary" onClick={() => setMode("VERIFIED")}>Verify payment</button>
      </div>}
    </section>

    <div className="payment-detail-grid">
      <section className="course-panel"><h2>Student</h2>
        <dl className="payment-definition"><div><dt>Name</dt><dd>{fullName(student)}</dd></div>
          <div><dt>Email</dt><dd>{student?.email || "—"}</dd></div><div><dt>Student ID</dt><dd>{item.student_id}</dd></div></dl></section>
      <section className="course-panel"><h2>Course provider</h2>
        <dl className="payment-definition"><div><dt>Provider</dt><dd>{item.provider?.name || "—"}</dd></div>
          <div><dt>Type</dt><dd className="text-capitalize">{String(item.provider?.type || "").toLowerCase()}</dd></div>
          <div><dt>Assigned guru</dt><dd>{item.default_guru?.name || "Not assigned"}</dd></div></dl></section>
      <section className="course-panel"><h2>Payment evidence</h2>
        <dl className="payment-definition"><div><dt>Method</dt><dd>{item.payment?.method || "—"}</dd></div>
          <div><dt>Reference</dt><dd>{item.payment?.offline_reference || "—"}</dd></div>
          <div><dt>Submitted</dt><dd>{displayDate(item.payment?.submitted_at, true)}</dd></div></dl>
        {item.payment?.proof_url && <a className="btn btn-sm btn-outline-primary mt-3" href={item.payment.proof_url}
          target="_blank" rel="noreferrer"><i className="bi bi-box-arrow-up-right me-2" />Open payment proof</a>}</section>
      <section className="course-panel"><h2>Enrollment</h2>
        <dl className="payment-definition"><div><dt>Status</dt><dd>{item.enrollment_status}</dd></div>
          <div><dt>Valid from</dt><dd>{displayDate(item.valid_from)}</dd></div>
          <div><dt>Valid until</dt><dd>{displayDate(item.valid_until)}</dd></div></dl>
        {item.payment?.rejection_reason && <div className="alert alert-danger mt-3 mb-0"><strong>Rejection reason</strong><br />{item.payment.rejection_reason}</div>}</section>
    </div>

    {mode && <div className="payment-modal-backdrop" role="presentation" onMouseDown={() => !saving && setMode(null)}>
      <form className="payment-modal" onSubmit={submitDecision} onMouseDown={(event) => event.stopPropagation()}>
        <div className="payment-modal-icon"><i className={`bi ${mode === "VERIFIED" ? "bi-shield-check" : "bi-x-octagon"}`} /></div>
        <h2>{mode === "VERIFIED" ? "Verify this payment?" : "Reject this payment?"}</h2>
        <p className="text-muted">{mode === "VERIFIED" ? "This activates the enrollment and cannot be repeated." : "The student can correct the payment information and submit it again."}</p>
        {mode === "VERIFIED" && isOrganization && <label className="course-form-field text-start"><span>Enrollment guru <em>*</em></span>
          <select className="form-select" value={guruId} onChange={(event) => setGuruId(event.target.value)} required>
            <option value="">Select eligible guru</option>{(item.eligible_gurus || []).map((guru) =>
              <option key={guru.id || guru.guru_id} value={guru.id || guru.guru_id}>{guru.name || guru.guru_name}</option>)}</select>
          <small>Only active organization gurus matching the course music field are available.</small></label>}
        {mode === "REJECTED" && <label className="course-form-field text-start"><span>Rejection reason <em>*</em></span>
          <textarea className="form-control" rows="3" value={reason} onChange={(event) => setReason(event.target.value)} required /></label>}
        <div className="payment-modal-actions"><button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={() => setMode(null)}>Cancel</button>
          <button className={`btn ${mode === "VERIFIED" ? "btn-primary" : "btn-danger"}`} disabled={saving}>{saving ? "Saving…" : mode === "VERIFIED" ? "Confirm verification" : "Reject payment"}</button></div>
      </form></div>}
  </main>;
}
